import * as React from 'react';
import { theme } from '../../../tokens';
import {
  card as cardCls,
  cardSection as sectionCls,
  header as headerCls,
  title as titleCls,
  meta as metaCls,
  content as contentCls,
  actions as actionsCls,
  media as mediaCls,
  divider as dividerCls,
} from './Card.css';

export type CardElevation = 0 | 1 | 2 | 3;
export type CardPadding = 'none' | 'sm' | 'md' | 'lg';

export type CardSpineTone =
  | 'neutral'
  | 'info'
  | 'success'
  | 'warn'
  | 'danger'
  | 'agent'
  | (string & {});

export interface CardProps extends React.HTMLAttributes<HTMLDivElement> {
  elevation?: CardElevation;
  padding?: CardPadding;
  /**
   * Compact-card shape (10px radius instead of the default 12px). Used
   * for memory cards, artifact cards, and file cards in rail panels.
   */
  compact?: boolean;
  /**
   * Optional left-edge stripe. Either a semantic tone key or an explicit
   * hex / CSS colour. Tone keys map to Genesis accent tokens.
   */
  spine?: CardSpineTone;
  /**
   * Add hover + cursor + focus ring behavior. The card becomes a
   * `role="button"` with `tabIndex=0`, and Enter / Space activate its
   * `onClick` (so it's keyboard-operable, not just clickable).
   */
  interactive?: boolean;
  as?: React.ElementType;
  /** React 19 ref-as-prop. */
  ref?: React.Ref<HTMLDivElement>;
}

function resolveSpine(spine: CardSpineTone): string {
  if (spine.startsWith('#') || spine.startsWith('rgb')) return spine;
  switch (spine) {
    case 'info':
      return theme.color.accent.blue[500];
    case 'success':
      return theme.color.accent.green[500];
    case 'warn':
      return theme.color.accent.amber[500];
    case 'danger':
      return theme.color.accent.red[500];
    case 'agent':
      return theme.color.accent.purple[500];
    case 'neutral':
      return theme.color.neutral.muted;
    default:
      return spine;
  }
}

// Subcomponents are attached to `Card.X` below — they're public surface
// only via the dotted access. Each accepts a `ref` per the React 19
// ref-as-prop convention so consumers can imperatively focus or scroll
// to specific Card slots (virtualised lists, focus management, etc.).

interface CardSlotProps<E extends HTMLElement> extends React.HTMLAttributes<E> {
  ref?: React.Ref<E>;
}

export function Header({ ref, className, ...rest }: CardSlotProps<HTMLDivElement>) {
  return <div ref={ref} className={[headerCls, className].filter(Boolean).join(' ')} {...rest} />;
}
Header.displayName = 'Card.Header';

export function Title({ ref, className, children, ...rest }: CardSlotProps<HTMLHeadingElement>) {
  return (
    <h3 ref={ref} className={[titleCls, className].filter(Boolean).join(' ')} {...rest}>
      {children}
    </h3>
  );
}
Title.displayName = 'Card.Title';

export function Meta({ ref, className, ...rest }: CardSlotProps<HTMLDivElement>) {
  return <div ref={ref} className={[metaCls, className].filter(Boolean).join(' ')} {...rest} />;
}
Meta.displayName = 'Card.Meta';

export function Content({ ref, className, ...rest }: CardSlotProps<HTMLDivElement>) {
  return <div ref={ref} className={[contentCls, className].filter(Boolean).join(' ')} {...rest} />;
}
Content.displayName = 'Card.Content';

export function Actions({ ref, className, ...rest }: CardSlotProps<HTMLDivElement>) {
  return <div ref={ref} className={[actionsCls, className].filter(Boolean).join(' ')} {...rest} />;
}
Actions.displayName = 'Card.Actions';

export function Media({ ref, className, ...rest }: CardSlotProps<HTMLDivElement>) {
  return <div ref={ref} className={[mediaCls, className].filter(Boolean).join(' ')} {...rest} />;
}
Media.displayName = 'Card.Media';

export function Divider({ ref, className, ...rest }: CardSlotProps<HTMLHRElement>) {
  return <hr ref={ref} className={[dividerCls, className].filter(Boolean).join(' ')} {...rest} />;
}
Divider.displayName = 'Card.Divider';

export interface SectionProps extends React.HTMLAttributes<HTMLDivElement> {
  padding?: CardPadding;
  ref?: React.Ref<HTMLDivElement>;
}

export function Section({ ref, padding = 'md', className, ...rest }: SectionProps) {
  return (
    <div
      ref={ref}
      className={[sectionCls({ padding }), className].filter(Boolean).join(' ')}
      {...rest}
    />
  );
}
Section.displayName = 'Card.Section';

export function Card({
  elevation = 0,
  padding = 'md',
  compact = false,
  spine,
  interactive = false,
  as: Component = 'div',
  children,
  onClick,
  onKeyDown,
  ref,
  className,
  style,
  ...rest
}: CardProps) {
  const cls = [
    cardCls({ elevation, padding, compact, spine: spine !== undefined, interactive }),
    className,
  ]
    .filter(Boolean)
    .join(' ');

  const spineStyle =
    spine !== undefined
      ? ({ ['--card-spine' as string]: resolveSpine(spine) } as React.CSSProperties)
      : undefined;

  // A `div[role="button"]` does not fire `onClick` on Enter / Space the
  // way a native <button> does, so an interactive Card needs its own
  // keyboard activation to be operable for keyboard + screen-reader
  // users (matching Tag's precedent). Enter activates on keydown; Space
  // activates on keyup after suppressing the default scroll on keydown.
  const handleKeyDown: React.KeyboardEventHandler<HTMLDivElement> = (e) => {
    if (interactive && onClick && (e.key === 'Enter' || e.key === ' ')) {
      // Prevent Space from scrolling the page / Enter from submitting a
      // surrounding form; mirror a native button activating on the key.
      e.preventDefault();
      onClick(e as unknown as React.MouseEvent<HTMLDivElement>);
    }
    onKeyDown?.(e);
  };

  return (
    <Component
      ref={ref}
      className={cls}
      tabIndex={interactive ? 0 : undefined}
      role={interactive ? 'button' : undefined}
      onClick={onClick}
      onKeyDown={handleKeyDown}
      style={{ ...spineStyle, ...style }}
      {...rest}
    >
      {children}
    </Component>
  );
}

Card.Header = Header;
Card.Title = Title;
Card.Meta = Meta;
Card.Content = Content;
Card.Actions = Actions;
Card.Media = Media;
Card.Divider = Divider;
Card.Section = Section;
Card.displayName = 'Card';
